// client/src/routes/portal/meters.tsx — SCR-PORT-003 „Utilități" · PORTAL-002 phase 2, D-2196.
//
// §8.16: the authenticated twin of SCR-LINK-001, photo-first (§11.2's flow P4). Built over
// PORTAL-001's `/portal/contracts/{id}/meters` and `/portal/contracts/{id}/meter-readings`, with
// `PhotoCaptureInput` — the §5.5 control EXP-005 wrote and MAINT-008 first made live — going
// through the portal's own upload route rather than the vault's (D-1182).
//
// The screen's own URL state is three keys — which page of the meter list is shown, and which
// billing month the bills section under it is showing (D-2197) — declared here the way „Chiria
// mea" declares its page and filter (§9.2.3). `contract` is on the layout route's schema and is
// carried, never redeclared.
//
// The bills section is withdrawn from view (D-2700): while `utilityPaymentsVisible()` answers
// `false` the screen is the meters alone, as it was before D-2196, and `year`/`month` stay
// declared so a link written while the section was on still parses.
//
// Under the lease's meters, „Contoarele clădirii" (D-3674, the building-lease-link plan's §3.4
// surface 3): the meters a clădire's administrator manages for the tenant's apartment, for a
// person who lives in one.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { utilityPaymentsVisible } from '@/config/utility-payments';
import { BuildingMetersSection } from '@/features/buildings/my/BuildingMetersSection';
import { ContractScope } from '@/features/portal/ContractScope';
import { MeterSubmission } from '@/features/portal/MeterSubmission';
import { PortalSkeleton } from '@/features/portal/PortalSkeletons';
import { portalMetersSearchSchema, type PortalMetersSearch } from '@/features/portal/search';

export const Route = createFileRoute('/portal/meters')({
  validateSearch: portalMetersSearchSchema,
  component: PortalMetersScreen,
});

function PortalMetersScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const withBills = utilityPaymentsVisible();

  return (
    <>
      <PageHeader title={t('portal.meters.title')} description={t('portal.meters.subtitle')} />
      {/* Both surfaces the screen lands with: the meters and, under them, the bills (D-2258) —
          the second only while the payments are on screen (D-2700). The meters shape draws its
          own panel and cards, because the screen is no longer one card (D-3523). */}
      <ContractScope
        pending={
          <div className="space-y-6">
            <PortalSkeleton shape="meters" count={3} />
            {withBills ? <PortalSkeleton shape="bills" count={3} className="portal-card" /> : null}
          </div>
        }
      >
        {(contract) => (
          <MeterSubmission
            contract={contract}
            // The bills section is this screen's, and only this screen's: its month lives in
            // the `year`/`month` search params declared above (D-2197), which is URL state the
            // other host of this component does not carry. Off while the payments are
            // withdrawn from view (D-2700).
            withBills={withBills}
            search={search}
            onSearchChange={(next: PortalMetersSearch) => {
              // The whole search is preserved and this screen's own key replaced, so the §6.6
              // `contract` a tenant chose survives a page turn (D-1180).
              // `resetScroll: false` so the PAGER owns the scroll. `main.tsx` creates the
              // router with `scrollRestoration: true`, which jumps the page to the top the
              // instant a search param changes — measured at 216 px to 0 between two 50 ms
              // samples — and that jump beat the pager's smooth scroll every time. The router
              // no longer moves the page on these navigations; `PortalPager` animates it
              // instead, and a filter change simply leaves the reader where they were
              // (D-1674).
              void navigate({
                search: (previous) => ({ ...previous, ...next }),
                resetScroll: false,
              });
            }}
          />
        )}
      </ContractScope>
      {/* The clădire's meters, under the lease's own and apart from them (R1, D-3674): one
          reading store, `app.building_readings`, shown here for submission only. Rendered only
          for a person who lives in a clădire. */}
      <BuildingMetersSection className="mt-6" />
    </>
  );
}
